/**
 * Normalizes markdown content for proper rendering.
 * 
 * Handles three cases:
 * 1. Already well-formatted markdown (passes through with light cleanup)
 * 2. Markdown with missing line breaks (inserts proper \n\n before markers)
 * 3. Plain text with NO markdown markers (intelligently converts to markdown)
 */
export function normalizeMarkdown(raw: string, force: boolean = false): string {
  if (!raw) return '';

  let text = raw.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

  // Force mode: always run the full plain-text-to-markdown conversion
  // Used by the "Reformat All Posts" admin button
  if (force) {
    // Strip any existing markdown heading markers so we start fresh
    const stripped = text
      .replace(/^#{1,6}\s+/gm, '')
      .replace(/^[-*]\s+/gm, '')
      .replace(/^\d+\.\s+/gm, '')
      .replace(/\*\*/g, '')
      .replace(/\*/g, '')
      .replace(/\n{2,}/g, ' ')
      .replace(/\n/g, ' ')
      .replace(/\s{2,}/g, ' ')
      .trim();
    return lightCleanup(convertPlainTextToMarkdown(stripped));
  }

  // Count existing formatting signals
  const newlineCount = (text.match(/\n/g) || []).length;
  const hasMarkdownHeadings = /^#{1,6}\s/m.test(text);
  const hasMarkdownLists = /^[-*]\s/m.test(text) || /^\d+\.\s/m.test(text);
  const isWellFormatted = text.length > 200 && newlineCount > 10;

  // CASE 1: Already well-formatted markdown
  if (isWellFormatted && hasMarkdownHeadings) {
    return lightCleanup(text);
  }

  // CASE 2: Has some markdown markers but missing line breaks
  if (hasMarkdownHeadings || hasMarkdownLists) {
    return lightCleanup(fixMarkdownLineBreaks(text));
  }

  // CASE 3: Plain text with no markdown — convert intelligently
  return lightCleanup(convertPlainTextToMarkdown(text));
}

/**
 * Fix markdown that has markers but missing line breaks
 */
function fixMarkdownLineBreaks(text: string): string {
  // Insert \n\n before heading markers that appear inline
  text = text.replace(/([^\n])\s*(#{1,6}\s)/g, '$1\n\n$2');
  // Insert \n\n after heading lines
  text = text.replace(/(#{1,6}\s[^\n]+)\n(?!\n)/g, '$1\n\n');
  // Insert \n before list items
  text = text.replace(/([^\n])\s+([-*]\s)/g, '$1\n$2');
  text = text.replace(/([^\n])\s+(\d+\.\s)/g, '$1\n$2');
  // Blank line before first list item
  text = text.replace(/([^\n\-*\d])\n([-*]\s)/g, '$1\n\n$2');
  // Blank line before blockquotes
  text = text.replace(/([^\n])\n(>\s)/g, '$1\n\n$2');
  // Blank line before code blocks
  text = text.replace(/([^\n])\n(```)/g, '$1\n\n$2');
  return text;
}

/**
 * Convert plain text (no markdown markers) into structured markdown.
 * Uses heuristics to detect headings, subheadings, and list items.
 */
function convertPlainTextToMarkdown(text: string): string {
  // Split into sentences/phrases. First try splitting on existing newlines,
  // then if it's all one line, split on sentence boundaries.
  let segments: string[];

  if ((text.match(/\n/g) || []).length > 3) {
    segments = text.split('\n').map(s => s.trim()).filter(Boolean);
  } else {
    // Split on sentence boundaries — period/question/exclamation followed by space and uppercase
    // Also split before short Title Case phrases that look like headings
    segments = splitIntoSegments(text);
  }

  const lines: string[] = [];
  let prevType: 'heading' | 'subheading' | 'list' | 'paragraph' | '' = '';

  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i].trim();
    if (!seg) continue;

    const type = classifySegment(seg, i, segments);

    // Add blank line between different types
    if (lines.length > 0 && type !== prevType) {
      lines.push('');
    }

    switch (type) {
      case 'heading':
        if (i === 0) {
          lines.push(`# ${seg}`);
        } else {
          lines.push(`## ${seg}`);
        }
        break;
      case 'subheading':
        lines.push(`### ${seg}`);
        break;
      case 'list':
        lines.push(`- ${seg}`);
        break;
      case 'paragraph':
      default:
        lines.push(seg);
        break;
    }

    prevType = type;
  }

  return lines.join('\n');
}

/**
 * Split a flat string into logical segments (sentences and phrases)
 */
function splitIntoSegments(text: string): string[] {
  const segments: string[] = [];
  // Split on: sentence end (.!?) followed by space + uppercase letter
  // This preserves abbreviations like "e.g." and "U.S." better
  const parts = text.split(/(?<=[.!?])\s+(?=[A-Z])/);

  for (const part of parts) {
    // Further split if a part contains a colon followed by a list-like structure
    // e.g., "This includes: Trade shows and conferences Retail locations"
    if (part.includes(':') && part.length > 100) {
      const colonIdx = part.indexOf(':');
      const before = part.substring(0, colonIdx + 1).trim();
      const after = part.substring(colonIdx + 1).trim();
      segments.push(before);

      // Split the "after" part on capitalized phrases that look like list items
      const listItems = after.split(/(?=[A-Z][a-z]{2,}(?:\s[a-z]+)*(?:\s[A-Z]|$))/).filter(Boolean);
      if (listItems.length > 2) {
        segments.push(...listItems.map(s => s.trim()).filter(Boolean));
      } else {
        segments.push(after);
      }
    } else {
      segments.push(part.trim());
    }
  }

  return segments.filter(Boolean);
}

/**
 * Classify a text segment as heading, subheading, list item, or paragraph
 */
function classifySegment(
  seg: string,
  index: number,
  allSegments: string[]
): 'heading' | 'subheading' | 'list' | 'paragraph' {
  const wordCount = seg.split(/\s+/).length;
  const endsWithPunctuation = /[.!?]$/.test(seg);
  const endsWithColon = seg.endsWith(':');
  const isTitleCase = /^[A-Z][a-z]+(\s+[A-Za-z][a-z]*)*$/.test(seg) && !endsWithPunctuation;
  const isShort = wordCount <= 8;
  const isVeryShort = wordCount <= 5;

  // First segment and it's short + title case = main heading
  if (index === 0 && isShort && !endsWithPunctuation) {
    return 'heading';
  }

  // Short title-case phrase without ending punctuation = heading/subheading
  if (isTitleCase && isShort && !endsWithPunctuation) {
    // Check if next segment is also short (suggesting this is a subheading before a list)
    const nextSeg = allSegments[index + 1]?.trim();
    if (nextSeg && nextSeg.split(/\s+/).length <= 6 && !/[.!?]$/.test(nextSeg)) {
      return 'subheading';
    }
    return 'heading';
  }

  // Ends with colon = subheading introducing a list
  if (endsWithColon && wordCount <= 12) {
    return 'subheading';
  }

  // Very short phrase (1-5 words) without sentence-ending punctuation,
  // especially when surrounded by similar short phrases = list item
  if (isVeryShort && !endsWithPunctuation && !endsWithColon) {
    const prevSeg = index > 0 ? allSegments[index - 1]?.trim() : '';
    const nextSeg = allSegments[index + 1]?.trim();
    const prevIsShort = prevSeg && prevSeg.split(/\s+/).length <= 6;
    const nextIsShort = nextSeg && nextSeg.split(/\s+/).length <= 6;
    const prevEndsColon = prevSeg?.endsWith(':');

    if (prevEndsColon || prevIsShort || nextIsShort) {
      return 'list';
    }
  }

  // Short phrase without punctuation after a colon-ending segment = list item
  if (!endsWithPunctuation && isShort) {
    for (let j = index - 1; j >= Math.max(0, index - 8); j--) {
      if (allSegments[j]?.trim().endsWith(':')) {
        return 'list';
      }
      if (allSegments[j]?.trim().split(/\s+/).length > 8) {
        break;
      }
    }
  }

  return 'paragraph';
}

/**
 * Light cleanup: collapse excessive newlines, trim
 */
function lightCleanup(text: string): string {
  return text.replace(/\n{3,}/g, '\n\n').trim();
}
